import type { Order, OrderStatus } from "@bioflow/contracts";
import { Check } from "lucide-react";
import { ORDER_STAGES, ORDER_STATUS_LABELS } from "../content/checkout";
import { formatMoney } from "../lib/money";

const STATUS_TONE: Record<OrderStatus, string> = {
  new: "badge--warn",
  confirmed: "badge--accent",
  shipped: "badge--accent",
  delivered: "badge--ok",
  cancelled: "badge--danger",
};

export function StatusBadge({ status }: { status: OrderStatus }) {
  return <span className={`badge ${STATUS_TONE[status]}`}>{ORDER_STATUS_LABELS[status]}</span>;
}

/** Позиции и итог — общий блок экрана заказа и карточки в админке. */
export function OrderLines({ order }: { order: Order }) {
  return (
    <>
      {order.items.map((item) => (
        <div key={item.variantId} className="row">
          <div className="row__body">
            <div className="row__title">
              {item.productTitle} · {item.variantLabel}
            </div>
            <div className="row__meta">
              {formatMoney(item.unitPrice)} × {item.quantity}
            </div>
          </div>
          <strong style={{ alignSelf: "center" }}>{formatMoney(item.lineTotal)}</strong>
        </div>
      ))}

      <div className="totals">
        <div className="totals__line">
          <span>Товары</span>
          <span>{formatMoney(order.subtotal)}</span>
        </div>
        <div className="totals__line">
          <span>Доставка</span>
          <span>{order.shipping.amountMinor === 0 ? "уточнит менеджер" : formatMoney(order.shipping)}</span>
        </div>
        <div className="totals__line totals__line--final">
          <span>Итого</span>
          <span>{formatMoney(order.total)}</span>
        </div>
      </div>
    </>
  );
}

/**
 * Шкала статусов «Оформлен → Подтверждён → Отправлен → Доставлен».
 * Отменённый заказ шкалу не показывает — у него нет «следующего шага».
 */
export function OrderProgress({ status }: { status: OrderStatus }) {
  if (status === "cancelled") return null;
  const current = ORDER_STAGES.indexOf(status);

  return (
    <ol className="progress" aria-label="Статус заказа">
      {ORDER_STAGES.map((stage, index) => {
        const state = index < current ? "done" : index === current ? "current" : "next";
        return (
          <li key={stage} className={`progress__step progress__step--${state}`}>
            <span className="progress__dot">
              {state === "done" && <Check size={12} strokeWidth={3} aria-hidden />}
            </span>
            <span className="progress__label">{ORDER_STATUS_LABELS[stage]}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** Контакты и доставка — как их ввёл покупатель. */
export function OrderContact({ order }: { order: Order }) {
  return (
    <div className="small">
      <div>
        <strong>{order.contact.name}</strong> · {order.contact.phone}
      </div>
      {order.contact.telegram && <div>{order.contact.telegram}</div>}
      <div className="muted" style={{ marginTop: 4 }}>
        {order.delivery.city}, {order.delivery.address}
      </div>
      {order.promoCode && <div className="muted">Промокод: {order.promoCode}</div>}
      {order.comment && <div className="muted" style={{ whiteSpace: "pre-wrap" }}>Комментарий: {order.comment}</div>}
    </div>
  );
}
